import { useRef, useState, useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { Search, X } from 'lucide-react'
import { fetchProducts } from '../../api/products'

export interface Suggestion {
  slug: string
  normalized_mpn: string
  display_title: string
}

interface Props {
  value: string
  onChange: (value: string) => void
  onSubmit: (value: string) => void
  onSelectSuggestion: (suggestion: Suggestion) => void
  placeholder?: string
  inputClassName?: string
  /**
   * Focus the input on mount. Client-only (runs in an effect, so SSR markup is
   * unaffected). The caller decides *when* to pass this — e.g. the homepage hero
   * only autofocuses on desktop, never on touch/mobile where it would pop the
   * keyboard over the fold.
   */
  autoFocus?: boolean
}

/**
 * Search input with typeahead suggestions (WEB-264).
 * Debounces the suggestion query at 300ms (accepts externally controlled value
 * so the parent can sync it with URL params independently).
 * Keyboard: ArrowDown/Up to navigate, Enter to confirm, Escape to dismiss.
 */
export default function SearchAutocompleteBox({
  value,
  onChange,
  onSubmit,
  onSelectSuggestion,
  placeholder,
  inputClassName,
  autoFocus,
}: Props) {
  const { t } = useTranslation()
  const resolvedPlaceholder = placeholder ?? t('product.searchPlaceholder')
  const [open, setOpen] = useState(false)
  const [focusedIndex, setFocusedIndex] = useState(-1)
  const containerRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (autoFocus) inputRef.current?.focus()
  }, [autoFocus])
  const debouncedQuery = useDebounce(value, 300)
  const location = useLocation()

  // Close dropdown on any route change so the overlay doesn't block the
  // page the user just navigated to (especially the filter button on mobile).
  useEffect(() => {
    setOpen(false)
    setFocusedIndex(-1)
  }, [location.pathname])

  const { data } = useQuery({
    queryKey: ['product-suggestions', debouncedQuery],
    queryFn: () => fetchProducts({ search: debouncedQuery, page_size: 8 }),
    enabled: debouncedQuery.trim().length >= 2,
    staleTime: 30_000,
  })
  const suggestions: Suggestion[] = (data?.data ?? []).map(p => ({
    slug: p.slug,
    normalized_mpn: p.normalized_mpn,
    display_title: p.display_title,
  }))

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setOpen(suggestions.length > 0 && value.trim().length >= 2)
    setFocusedIndex(-1)
  }, [suggestions.length, value])

  useEffect(() => {
    function onMouseDown(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', onMouseDown)
    return () => document.removeEventListener('mousedown', onMouseDown)
  }, [])

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      if (open) setFocusedIndex(i => Math.min(i + 1, suggestions.length - 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setFocusedIndex(i => Math.max(i - 1, -1))
    } else if (e.key === 'Enter') {
      if (focusedIndex >= 0 && suggestions[focusedIndex]) {
        e.preventDefault()
        onSelectSuggestion(suggestions[focusedIndex])
        setOpen(false)
      } else {
        onSubmit(value)
        setOpen(false)
      }
    } else if (e.key === 'Escape') {
      setOpen(false)
      setFocusedIndex(-1)
    }
  }

  return (
    <div ref={containerRef} className="relative w-full">
      <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-neutral-400 pointer-events-none" />
      <input
        ref={inputRef}
        type="text"
        value={value}
        onChange={e => onChange(e.target.value)}
        onKeyDown={handleKeyDown}
        onFocus={() => {
          if (suggestions.length > 0 && value.trim().length >= 2) setOpen(true)
        }}
        placeholder={resolvedPlaceholder}
        autoComplete="off"
        aria-autocomplete="list"
        aria-expanded={open}
        aria-haspopup="listbox"
        role="combobox"
        className={inputClassName}
      />
      {value && (
        <button
          type="button"
          onClick={() => { onChange(''); onSubmit(''); setOpen(false); }}
          className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 rounded hover:bg-neutral-100 text-neutral-400"
          aria-label={t('product.clearSearch')}
        >
          <X className="h-3.5 w-3.5" />
        </button>
      )}
      {open && (
        <ul
          role="listbox"
          className="absolute top-full left-0 right-0 z-50 mt-1 bg-white rounded-md shadow-lg border border-neutral-200 overflow-hidden max-h-72 overflow-y-auto"
        >
          {suggestions.map((s, i) => (
            <li
              key={s.slug}
              role="option"
              aria-selected={i === focusedIndex}
              onMouseDown={e => e.preventDefault()}
              onClick={() => { onSelectSuggestion(s); setOpen(false); }}
              className={`flex items-center gap-3 px-3 py-2.5 cursor-pointer ${i === focusedIndex ? 'bg-primary/10 text-primary' : 'hover:bg-neutral-50'}`}
            >
              <span className="font-mono text-xs text-neutral-400 shrink-0 min-w-0">{s.normalized_mpn}</span>
              {s.display_title !== s.normalized_mpn && (
                <span className="text-sm text-neutral-700 truncate">{s.display_title}</span>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

function useDebounce<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = useState(value)
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay)
    return () => clearTimeout(t)
  }, [value, delay])
  return debounced
}
